import type { MouseEvent } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { AssistIcon, GoalIcon, MissedPenaltyIcon } from './icons';
import PriceChangeTile from './price-change-tile';
import { PlayerKit } from '@components/player/player-kit';
import Icon from '@components/atoms/icon';
import { getPredictedPointsLevelStyles } from '@components/fixtures/utils/fixtureTypes';
import {
  getAvailabilityLabel,
  getAvailabilitySeverity
} from '@lib/my-team/player-availability';
import type { PlayerPriceChange } from '@api-service/players';
import type {
  AvailabilitySeverity,
  PlayerStatus
} from '@lib/my-team/player-availability';

const DEFAULT_MAX_FIXTURE_TILES = 3;

export type PitchPlayerFixture = {
  /** Gameweek the fixture belongs to; drives per-gameweek prediction locks. */
  gameweek?: number;
  opponent: { shortName: string };
  isHome: boolean;
  predictedPoints?: number | null;
};

export type PitchPlayerLiveEvents = {
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
  missedPenalties: number;
  /** Awarded bonus points (0-3). Renders as a coloured numbered chip. */
  bonusPoints: number;
};

export type PitchPlayerProps = {
  name: string;
  teamShortName: string;
  /**
   * CDN-hosted team kit image from the ffh-backend API (goalkeeper variant
   * already chosen server-side). Falls back to the bundled blank shirt when
   * absent.
   */
  kitImageUrl?: string;
  price: number;
  fixtures?: PitchPlayerFixture[];
  maxFixtures?: number;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
  isIncoming?: boolean;
  isOutgoing?: boolean;
  forceShowQuickActions?: boolean;
  /**
   * When set, the tile renders in live mode: the bottom info area
   * shows live points instead of price, and the fixture tile grid is
   * hidden. Combine with `liveEvents` to render the event icon strip.
   */
  livePoints?: number;
  liveEvents?: PitchPlayerLiveEvents;
  /** Picks with no fixture this gameweek (blank gameweek). */
  isFaded?: boolean;
  /**
   * The points and events on this tile came from a simulated fixture, so they
   * are fabricated. Marks the tile in live mode, which is the only mode that
   * renders those figures.
   */
  isSimulated?: boolean;
  /**
   * When true the tile shows the player's predicted price change (direction,
   * estimated change time and progress) in place of the fixture grid.
   */
  isPriceChangeView?: boolean;
  priceChange?: PlayerPriceChange;
  isPriceChangeLoading?: boolean;
  /** FPL availability status; drives the red injury/warning treatment. */
  status?: PlayerStatus;
  chanceOfPlaying?: number | null;
  /**
   * Hides predicted-points figures (and their colour coding) on the fixture
   * tiles, leaving just the opponent and venue. Used for non-paying members
   * and the pre-gameweek-one build picker.
   */
  hidePredictedPoints?: boolean;
  /**
   * Per-gameweek prediction lock for non-paying members: tiles whose
   * gameweek this returns true for show a padlock in place of the predicted
   * points, while free gameweeks keep their figures and colour coding.
   */
  getIsFixturePredictionLocked?: (gameweek: number | undefined) => boolean;
  onMakeCaptain?: () => void;
  onMakeViceCaptain?: () => void;
  onTransferOut?: () => void;
  onCancelTransferOut?: () => void;
};

const BONUS_STYLE: Record<1 | 2 | 3, { bg: string; text: string }> = {
  // Scoreline-app's bps tokens, expressed as arbitrary Tailwind values
  // (the project's palette does not expose these hues directly).
  // Bronze, silver, gold. Text colour flips per tier so the numeral
  // stays readable on each background.
  1: { bg: 'bg-[#D07325]', text: 'text-white' },
  2: { bg: 'bg-[#E5E5E5]', text: 'text-neutral-800' },
  3: { bg: 'bg-[#FAC515]', text: 'text-neutral-900' }
};

const EventChip = ({
  children,
  className,
  label
}: {
  children: React.ReactNode;
  className?: string;
  label: string;
}) => (
  <span
    aria-label={label}
    className={classNames(
      'inline-flex items-center justify-center rounded-full w-4 h-4 bg-neutral-900 text-white shadow-sm shadow-neutral-900/30',
      className
    )}
  >
    {children}
  </span>
);

// Name-row treatment per availability severity. Caution is FPL's amber for a
// mild doubt; danger is the red used for injuries, suspensions and heavy doubts.
const AVAILABILITY_ROW_STYLES: Record<
  AvailabilitySeverity,
  { row: string; name: string; price: string }
> = {
  none: {
    row: 'bg-white',
    name: 'text-neutral-900',
    price: 'text-neutral-600'
  },
  caution: {
    row: 'bg-warning-400 text-neutral-900',
    name: 'text-neutral-900',
    price: 'text-neutral-800'
  },
  danger: {
    row: 'bg-danger-600 text-white',
    name: 'text-white',
    price: 'text-white'
  }
};

const CardChip = ({
  tone,
  label
}: {
  tone: 'yellow' | 'red';
  label: string;
}) => (
  <span
    aria-label={label}
    className={classNames(
      'inline-block w-[10px] h-[14px] rounded-[2px] shadow-sm shadow-neutral-900/20',
      tone === 'yellow' ? 'bg-warning-400' : 'bg-danger-500'
    )}
  />
);

const hasAnyLiveEvents = (events: PitchPlayerLiveEvents): boolean =>
  events.goals > 0 ||
  events.assists > 0 ||
  events.yellowCards > 0 ||
  events.redCards > 0 ||
  events.missedPenalties > 0 ||
  events.bonusPoints > 0;

const LiveEventIcons = ({ events }: { events: PitchPlayerLiveEvents }) => {
  const {
    goals,
    assists,
    yellowCards,
    redCards,
    missedPenalties,
    bonusPoints
  } = events;
  const bonusTier =
    bonusPoints === 1 || bonusPoints === 2 || bonusPoints === 3
      ? (bonusPoints as 1 | 2 | 3)
      : null;

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-0.5 leading-none"
      data-cy="pitch-player-events"
    >
      {Array.from({ length: yellowCards }).map((_, index) => (
        <CardChip key={`yc${index}`} tone="yellow" label="yellow card" />
      ))}
      {redCards > 0 && <CardChip tone="red" label="red card" />}
      {Array.from({ length: goals }).map((_, index) => (
        <EventChip key={`g${index}`} label="goal">
          <GoalIcon size={10} />
        </EventChip>
      ))}
      {Array.from({ length: assists }).map((_, index) => (
        <EventChip key={`a${index}`} label="assist">
          <AssistIcon size={10} />
        </EventChip>
      ))}
      {Array.from({ length: missedPenalties }).map((_, index) => (
        <EventChip key={`mp${index}`} label="missed penalty">
          <MissedPenaltyIcon size={10} />
        </EventChip>
      ))}
      {bonusTier !== null && (
        <span
          aria-label={`${bonusTier} bonus points`}
          className={classNames(
            'inline-flex items-center justify-center rounded-full w-4 h-4 text-[10px] font-bold leading-none shadow-sm shadow-neutral-900/30 ring-1 ring-neutral-900/10',
            BONUS_STYLE[bonusTier].bg,
            BONUS_STYLE[bonusTier].text
          )}
          data-cy="pitch-player-bonus"
        >
          {bonusTier}
        </span>
      )}
    </div>
  );
};

const getLiveModePointsTone = (points: number): string => {
  if (points > 0) return 'text-success-600';
  if (points < 0) return 'text-danger-600';
  return 'text-neutral-600';
};

const stop = (event: MouseEvent) => {
  event.stopPropagation();
};

const PitchPlayer = ({
  name,
  teamShortName,
  kitImageUrl,
  price,
  fixtures = [],
  maxFixtures = DEFAULT_MAX_FIXTURE_TILES,
  isCaptain = false,
  isViceCaptain = false,
  isIncoming = false,
  isOutgoing = false,
  forceShowQuickActions = false,
  livePoints,
  liveEvents,
  isFaded = false,
  isSimulated = false,
  hidePredictedPoints = false,
  getIsFixturePredictionLocked,
  isPriceChangeView = false,
  priceChange,
  isPriceChangeLoading = false,
  status,
  chanceOfPlaying,
  onMakeCaptain,
  onMakeViceCaptain,
  onTransferOut,
  onCancelTransferOut
}: PitchPlayerProps) => {
  const isLiveMode = livePoints !== undefined;
  const showPriceChange = isPriceChangeView && !isLiveMode;
  const visibleFixtures =
    isLiveMode || showPriceChange ? [] : fixtures.slice(0, maxFixtures);
  const availabilitySeverity = getAvailabilitySeverity(status, chanceOfPlaying);
  const isUnavailable = availabilitySeverity !== 'none';
  const availabilityStyles = AVAILABILITY_ROW_STYLES[availabilitySeverity];
  const availabilityLabel = getAvailabilityLabel(status, chanceOfPlaying);
  const hasQuickActions =
    Boolean(onMakeCaptain) ||
    Boolean(onMakeViceCaptain) ||
    Boolean(onTransferOut) ||
    Boolean(onCancelTransferOut);

  // The captain/vice badge sits on the kit shoulder in the resting state, but
  // it shares that corner with the hover quick-action buttons. Yield to those:
  // fade out on hover (hover-capable devices) when actions reveal on hover, and
  // stay hidden entirely when they are always shown (the build screen).
  const displayBadgeClassName = classNames(
    'absolute -top-1 left-2 z-30 flex justify-center items-center rounded-full w-4 h-4 bg-neutral-800 p-[2px] pointer-events-none',
    hasQuickActions &&
      (forceShowQuickActions
        ? 'hidden'
        : 'transition-opacity [@media(hover:hover)]:group-hover:opacity-0')
  );

  return (
    <div
      className={classNames(
        'flex flex-col items-center group',
        isIncoming && 'ring-2 ring-success-500 rounded-lg',
        isOutgoing && 'opacity-50',
        isFaded && 'opacity-60'
      )}
      data-cy="pitch-player"
      data-cy-incoming={isIncoming ? 'true' : 'false'}
      data-cy-outgoing={isOutgoing ? 'true' : 'false'}
      data-cy-live={isLiveMode ? 'true' : 'false'}
      data-testid="pitch-player"
    >
      <div className="relative">
        <div className="w-[42px] h-[30px] md:w-[64px] md:h-[52px] relative flex items-center justify-center">
          <PlayerKit
            teamShortName={teamShortName}
            kitImageUrl={kitImageUrl}
            className="w-full h-full object-contain"
          />
        </div>
        {isCaptain && (
          <div className={displayBadgeClassName} data-cy="captain-icon">
            <Image
              src="/images/captain-armband.png"
              alt="captain"
              width={12}
              height={12}
            />
          </div>
        )}
        {isViceCaptain && (
          <div className={displayBadgeClassName} data-cy="vice-captain-icon">
            <Image
              src="/images/vice-captain-armband.png"
              alt="vice captain"
              width={12}
              height={12}
            />
          </div>
        )}

        {hasQuickActions && (
          <div
            className={`absolute inset-0 z-20 transition-opacity pointer-events-none ${
              forceShowQuickActions
                ? 'opacity-100'
                : // Hover-revealed actions only exist on hover-capable devices;
                  // on touch they would be invisible but still swallow taps, so
                  // they render display:none and the tap falls through to the
                  // tile, which opens the player action modal.
                  'hidden [@media(hover:hover)]:block opacity-0 group-hover:opacity-100 focus-within:opacity-100'
            }`}
            data-cy="pitch-player-quick-actions"
          >
            {onCancelTransferOut && (
              <button
                type="button"
                onClick={(event) => {
                  stop(event);
                  onCancelTransferOut();
                }}
                aria-label="Cancel transfer"
                data-cy="pitch-player-cancel-transfer"
                className="pointer-events-auto absolute -top-1 -right-1 flex justify-center items-center rounded-full w-5 h-5 bg-danger-600 text-white"
              >
                <span className="text-xs leading-none">×</span>
              </button>
            )}

            {!onCancelTransferOut && onMakeCaptain && !isCaptain && (
              <button
                type="button"
                onClick={(event) => {
                  stop(event);
                  onMakeCaptain();
                }}
                aria-label="Make captain"
                data-cy="pitch-player-make-captain"
                className="pointer-events-auto absolute top-0 left-0 flex justify-center items-center rounded-full w-4 h-4 bg-neutral-800 p-[2px]"
              >
                <Image
                  src="/images/captain-armband.png"
                  alt=""
                  width={12}
                  height={12}
                />
              </button>
            )}

            {!onCancelTransferOut &&
              onMakeViceCaptain &&
              !isViceCaptain &&
              !isCaptain && (
                <button
                  type="button"
                  onClick={(event) => {
                    stop(event);
                    onMakeViceCaptain();
                  }}
                  aria-label="Make vice-captain"
                  data-cy="pitch-player-make-vice-captain"
                  className="pointer-events-auto absolute top-0 left-[20px] flex justify-center items-center rounded-full w-4 h-4 bg-neutral-800 p-[2px]"
                >
                  <Image
                    src="/images/vice-captain-armband.png"
                    alt=""
                    width={12}
                    height={12}
                  />
                </button>
              )}

            {!onCancelTransferOut && onTransferOut && (
              <button
                type="button"
                onClick={(event) => {
                  stop(event);
                  onTransferOut();
                }}
                aria-label="Transfer out"
                data-cy="pitch-player-transfer-out"
                className="pointer-events-auto absolute top-0 right-0 flex justify-center items-center rounded-full w-4 h-4 bg-neutral-800 text-white"
              >
                <span className="text-[10px] leading-none">×</span>
              </button>
            )}
          </div>
        )}
      </div>

      {isLiveMode ? (
        <div className="relative w-full max-w-[112px] -mt-2">
          <div
            className={classNames(
              'w-full text-center rounded-xl overflow-hidden shadow-sm shadow-neutral-900/20',
              isSimulated && 'border-2 border-secondary-600'
            )}
          >
            {/* Secondary rather than warning or danger: those already mean
                "outage" and "problem" on this page, and a simulated fixture is
                neither. It has to read as "these numbers are made up". */}
            {isSimulated && (
              <div
                className="bg-secondary-600 text-white text-[8px] md:text-[9px] font-bold uppercase tracking-[0.12em] leading-none py-0.5"
                title="Simulated fixture: these points and events are fabricated"
                data-cy="pitch-player-simulated"
              >
                Sim
              </div>
            )}
            {/* The bottom padding is the half of the event icons that
                overlaps the card, so they never cover the points. */}
            <div className="bg-white px-1.5 pt-1.5 pb-2">
              <div
                className="text-[11px] md:text-xs font-semibold truncate text-neutral-900 leading-tight tracking-tight"
                title={name}
              >
                {name}
              </div>
              <div
                className={classNames(
                  'mt-0.5 flex items-baseline justify-center gap-1 leading-none',
                  getLiveModePointsTone(livePoints)
                )}
                data-cy="pitch-player-live-points"
              >
                <span className="text-sm md:text-base font-bold tabular-nums">
                  {livePoints}
                </span>
                <span className="text-[9px] font-medium uppercase tracking-[0.08em] text-neutral-400">
                  pts
                </span>
              </div>
            </div>
          </div>
          {/* Outside the card so its overflow clip doesn't cut the icons off
              where they hang below its edge, as they do on the app. Pinned by
              its top, like the app, so the first row always straddles the
              edge and a row that wraps hangs further down instead of pushing
              the first one up over the points. */}
          {liveEvents && hasAnyLiveEvents(liveEvents) && (
            <div
              className="pointer-events-none absolute inset-x-0 top-full z-10 -mt-2 flex justify-center"
              data-cy="pitch-player-events-anchor"
            >
              <LiveEventIcons events={liveEvents} />
            </div>
          )}
        </div>
      ) : (
        <div
          className="w-full -mt-2 relative rounded-lg overflow-hidden shadow-sm shadow-neutral-900/10"
          data-cy="pitch-player-card"
        >
          <div
            className={classNames(
              'flex items-baseline gap-1 px-1.5 py-0.5 md:px-2 md:py-1 w-full',
              availabilityStyles.row
            )}
            data-cy="pitch-player-name-status"
            data-cy-unavailable={isUnavailable ? 'true' : 'false'}
            data-cy-availability={availabilitySeverity}
          >
            {/* Centred on its own: the row aligns the name and price by
            baseline, which would sit the icon's bottom edge on the text's
            baseline and leave it floating high. */}
            {isUnavailable && (
              <Icon
                name="Warning"
                title={availabilityLabel ?? undefined}
                aria-label={availabilityLabel ?? 'Availability warning'}
                className="w-3 h-3 md:w-3.5 md:h-3.5 shrink-0 self-center"
              />
            )}
            <span
              className={classNames(
                'flex-1 min-w-0 text-left text-xs md:text-sm font-semibold truncate leading-tight',
                availabilityStyles.name
              )}
              title={name}
            >
              {name}
            </span>
            <span
              className={classNames(
                'shrink-0 text-[10px] md:text-xs',
                availabilityStyles.price
              )}
            >
              £{price.toFixed(1)}
            </span>
          </div>

          {showPriceChange ? (
            <PriceChangeTile
              priceChange={priceChange}
              isLoading={isPriceChangeLoading}
            />
          ) : (
            visibleFixtures.length > 0 && (
              <div
                className={classNames(
                  'w-full grid gap-0',
                  visibleFixtures.length === 1 && 'grid-cols-1',
                  visibleFixtures.length === 2 && 'grid-cols-2',
                  visibleFixtures.length === 3 && 'grid-cols-3'
                )}
              >
                {visibleFixtures.map((fixture, idx) => {
                  const points = fixture.predictedPoints ?? 0;
                  const isTilePredictionLocked =
                    !hidePredictedPoints &&
                    (getIsFixturePredictionLocked?.(fixture.gameweek) ?? false);
                  const shouldShowPoints =
                    !hidePredictedPoints && !isTilePredictionLocked;
                  const tileStyles = shouldShowPoints
                    ? getPredictedPointsLevelStyles(points)
                    : 'bg-neutral-100 text-neutral-700';
                  return (
                    <div
                      key={`${fixture.opponent.shortName}-${idx}`}
                      data-cy="pitch-player-fixture"
                      data-testid="pitch-player-fixture"
                      className={classNames(
                        'flex flex-col items-center justify-center px-px leading-[12px]',
                        tileStyles
                      )}
                    >
                      {isTilePredictionLocked && (
                        <span
                          className="pt-0.5 md:pt-1 leading-[14px]"
                          data-cy="pitch-player-fixture-lock"
                        >
                          <Icon name="Lock" fontSize="12px" />
                        </span>
                      )}
                      {shouldShowPoints && (
                        <span className="text-center text-xs md:text-sm font-semibold pt-0.5 md:pt-1 leading-[14px]">
                          {fixture.predictedPoints?.toFixed(1) ?? '-'}
                        </span>
                      )}
                      <span className="text-center text-[8px] md:text-[10px] pb-0.5 md:pb-1 leading-[12px]">
                        {fixture.opponent.shortName}
                        {fixture.isHome ? ' (H)' : ' (A)'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default PitchPlayer;
